'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';

export default function RegisterPage() {
  const { signUp, signInWithGoogle } = useAuth();
  const router = useRouter();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await signUp(email, password, { fullName, role: 'customer' });
      toast.success('Account created! Welcome to WorldwayLuxe.');
      router.push('/customer-portal');
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      const error = err as { message?: string };
      toast.error(error?.message || 'Google sign in failed.');
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background)] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl font-bold text-[var(--gold)] tracking-wide">
            WorldwayLuxe
          </h1>
          <p className="text-[var(--muted)] text-sm mt-2">Enterprise AI Travel Commerce</p>
        </div>

        <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-2xl p-8 shadow-2xl">
          <h2 className="text-xl font-semibold text-[var(--foreground)] mb-6">Create Account</h2>

          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm text-[var(--muted)] mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="Alexandra Thornton"
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-4 py-2.5 text-[var(--foreground)] text-sm placeholder-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--muted)] mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-4 py-2.5 text-[var(--foreground)] text-sm placeholder-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--muted)] mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Min. 8 characters"
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-4 py-2.5 text-[var(--foreground)] text-sm placeholder-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-[var(--muted)] mb-1.5">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Repeat password"
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-4 py-2.5 text-[var(--foreground)] text-sm placeholder-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[var(--gold)] text-[var(--background)] font-semibold py-2.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--border)]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[var(--surface-2)] px-3 text-[var(--muted)]">or</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg py-2.5 text-sm text-[var(--foreground)] hover:border-[var(--gold)] transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <p className="text-center text-sm text-[var(--muted)] mt-6">
            Already have an account?{' '}
            <Link href="/auth/login" className="text-[var(--gold)] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
